import { useLingui } from "@lingui/react/macro";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../base/command";
import { Popover, PopoverContent, PopoverTrigger } from "../base/popover";

export interface BaseComboboxOption<T> {
  value: T;
  label: string;
}

export interface ComboboxTriggerProps extends React.ComponentPropsWithoutRef<"button"> {
  /** The selected value's display text; when omitted, the muted placeholder shows. */
  label?: React.ReactNode;
  placeholder: string;
  open?: boolean;
}

/**
 * The shared, form-field-styled combobox trigger: a box showing the current
 * selection (or a muted placeholder) with a chevron. Designed as the `asChild`
 * target of a `PopoverTrigger`, so it forwards its ref and spreads the props
 * Radix injects (click handler, `data-state`, aria attributes) onto the button.
 */
export const ComboboxTrigger = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerProps
>(function ComboboxTrigger(
  { label, placeholder, open, className, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={open}
      data-slot="combobox"
      className={cn(
        "border-input dark:bg-input/30 inline-flex h-8 w-full min-w-[200px] items-center justify-between rounded-md border bg-transparent px-3 py-0 text-sm transition-[color,box-shadow] outline-none",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        "focus-visible:aria-invalid:ring-ring/50 focus-visible:aria-invalid:border-ring",
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span className={cn(label == null && "text-muted-foreground")}>
        {label ?? placeholder}
      </span>
      <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
    </button>
  );
});

export interface BaseComboboxProps<T> {
  id?: string;
  value?: T;
  options: BaseComboboxOption<T>[];
  onChange?: (value: T) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
}

function ComboboxInner<T>(
  {
    id,
    value,
    options,
    onChange,
    placeholder,
    searchPlaceholder,
    emptyText,
    disabled,
    className,
    "aria-invalid": ariaInvalid,
  }: BaseComboboxProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { t } = useLingui();
  const [open, setOpen] = React.useState(false);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ComboboxTrigger
          id={id}
          ref={ref}
          label={selectedOption?.label}
          placeholder={placeholder ?? t`Select...`}
          open={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder ?? t`Search...`} />
          <CommandList
            // The enclosing modal Dialog's scroll lock cancels wheel/touch
            // scroll outside itself via bubble-phase document listeners;
            // stopping propagation here lets the menu scroll natively.
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty>{emptyText ?? t`No results found.`}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={String(option.value)}
                  value={option.label}
                  onSelect={() => {
                    onChange?.(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  {value === option.value && (
                    <CheckIcon className="ml-auto size-4" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Use type assertion for generic forwardRef
export const BaseCombobox = React.forwardRef(ComboboxInner) as <T>(
  props: BaseComboboxProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => React.ReactElement;
